import { useState, useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { DsnFilterStateProps, useDsnFilterState } from '../../../hooks/useDsnFilterState';
import DsnFilterData from '../../../services/Filters/DsnFilterData';
import { INITIAL_QUERY_PARAMS, INITIAL_PAGINATION } from '../constants/dsnListState.constants';
import { QueryParams, ODataQueryResultsReturn, PaginationState } from '../types/odatadsn.types';
import { useQueryDsns } from '../../../hooks/useQueryDsns';
import { useLogger } from '../../../context/useLogger';
import { parseQueryDsnsResponse } from '../../../utils/odataResponseHelper';
import { getSelectedDateRange } from '../../../utils/dateHelpers';
import { ENV } from '../../../config/env';
import {
  DSN_SORT_COLUMNS,
  DSN_SORT_COLUMN_KEYS,
  DSN_SORT_STORAGE_KEY,
  INITIAL_DSN_SORT,
  type DsnSortColumn,
  type DsnSortDirection,
  type DsnSortCriterion,
  type DsnSortState,
} from '../dsnTableSorting';

type InitialPagination = Pick<PaginationState, 'currentPage' | 'pageSize'>;

const isDsnSortColumn = (value: unknown): value is DsnSortColumn =>
  typeof value === 'string' && DSN_SORT_COLUMN_KEYS.some(column => column === value);

const isDsnSortDirection = (value: unknown): value is DsnSortDirection =>
  value === 'ascending' || value === 'descending';

const isDsnSortCriterion = (value: unknown): value is DsnSortCriterion => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return isDsnSortColumn(candidate.column) && isDsnSortDirection(candidate.direction);
};

const deserializeDsnSort = (serialized: string): DsnSortState => {
  try {
    const value: unknown = JSON.parse(serialized);
    const candidates = Array.isArray(value) ? value : [value];
    const seenColumns = new Set<DsnSortColumn>();
    const restoredSort = candidates.filter((candidate): candidate is DsnSortCriterion => {
      if (!isDsnSortCriterion(candidate) || seenColumns.has(candidate.column)) {
        return false;
      }

      seenColumns.add(candidate.column);
      return true;
    });

    if (candidates.length > 0 && restoredSort.length === 0) {
      return INITIAL_DSN_SORT;
    }

    return ENV.DSN_MULTI_SORT_ENABLED ? restoredSort : restoredSort.slice(0, 1);
  } catch {
    return INITIAL_DSN_SORT;
  }
};

const ODataDsnsQueryResults = (initialPagination?: InitialPagination): ODataQueryResultsReturn => {
  const allowMultiSort = ENV.DSN_MULTI_SORT_ENABLED;
  const filterState: DsnFilterStateProps = useDsnFilterState();
  const [queryParams] = useState<QueryParams>(INITIAL_QUERY_PARAMS);
  const [sort, setSort] = useLocalStorage<DsnSortState>(DSN_SORT_STORAGE_KEY, INITIAL_DSN_SORT, {
    serializer: value => JSON.stringify(value),
    deserializer: deserializeDsnSort,
  });
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    ...INITIAL_PAGINATION,
    currentPage: initialPagination?.currentPage ?? filterState.currentPage,
    pageSize: initialPagination?.pageSize ?? filterState.pageSize,
  }));
  const logger = useLogger();
  const { start: startDate, end: endDate } = getSelectedDateRange(filterState.dateRangeValue);

  // Build an independent snapshot: previous query keys must never be mutated by a later render.
  const queryFilter = new DsnFilterData(logger)
    .addUserFilter(filterState.selectedUserIds, true)
    .addDateFilter(startDate, endDate, queryParams.isDate)
    .addTestFilter(filterState.isTest)
    .addStatusFilter(filterState.selectedStatus ?? '', !!filterState.selectedStatus)
    .addNatureFilter(filterState.selectedNatures, filterState.selectedNatures.length > 0)
    .addSearchFilter(filterState.searchTerm ?? '', (filterState.searchTerm ?? '') !== '')
    .getFilters();

  // React Query for fetching DSNs
  const queryResult = useQueryDsns(
    queryFilter,
    sort.map(
      ({ column, direction }) =>
        `${DSN_SORT_COLUMNS[column].odataField} ${direction === 'ascending' ? 'asc' : 'desc'}`,
    ),
    queryParams.expand,
    pagination.pageSize,
    pagination.currentPage,
  );

  const handleSort = useCallback(
    (column: DsnSortColumn, addToExisting = false) => {
      setSort(current => {
        if (allowMultiSort && addToExisting) {
          const existingIndex = current.findIndex(criterion => criterion.column === column);
          if (existingIndex === -1) {
            return [...current, { column, direction: 'ascending' }];
          }
          if (current[existingIndex].direction === 'ascending') {
            return current.map((criterion, index) =>
              index === existingIndex ? { ...criterion, direction: 'descending' } : criterion,
            );
          }
          return current.filter((_, index) => index !== existingIndex);
        }

        const direction =
          current[0]?.column === column && current[0].direction === 'ascending'
            ? 'descending'
            : 'ascending';

        return [{ column, direction }];
      });
    },
    [allowMultiSort, setSort],
  );

  const { data } = parseQueryDsnsResponse(queryResult);
  const loading = queryResult.isLoading;
  const error = queryResult.error?.toString() || null;

  logger.debug('Response data:', data);
  logger.debug('Response error:', error);
  logger.debug('Response loading:', loading);

  return {
    data,
    error,
    loading,
    pagination,
    setPagination,
    selectedUserIds: filterState.selectedUserIds,
    setSelectedUserIds: filterState.setSelectedUserIds,
    isTest: filterState.isTest,
    setIsTest: filterState.setIsTest,
    selectedStatus: filterState.selectedStatus,
    setSelectedStatus: filterState.setSelectedStatus,
    selectedNatures: filterState.selectedNatures,
    setSelectedNatures: filterState.setSelectedNatures,
    searchTerm: filterState.searchTerm,
    setSearchTerm: filterState.setSearchTerm,
    // Date range
    setDateRangeValue: filterState.setDateRangeValue,
    dateRangeValue: filterState.dateRangeValue,
    sort,
    handleSort,
  };
};

export default ODataDsnsQueryResults;
